/* src/components/product/ProductGrid.tsx */

"use client";

import Image from "next/image";
import Link from "next/link";
// 1. 移除 useState，因为分页状态将由父组件通过 props 控制
import { ChevronLeft, ChevronRight } from "lucide-react";

type ProductPreview = {
	id: string;
	name: string;
	imageUrl: string;
};

// 2. 定义新的 Props 类型，接收 currentPage 和 onPageChange
type ProductGridProps = {
	products: ProductPreview[];
	currentPage: number;
	onPageChange: (newPage: number) => void;
};

const ITEMS_PER_PAGE = 12;

// 3. 在组件参数中解构新的 props
export function ProductGrid({
	products,
	currentPage,
	onPageChange,
}: ProductGridProps) {
	// 4. 删除内部的 useState 定义
	// const [currentPage, setCurrentPage] = useState(1);

	// 分页逻辑现在完全依赖于从 props 传入的 currentPage
	const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = startIndex + ITEMS_PER_PAGE;
	const currentProducts = products.slice(startIndex, endIndex);

	if (products.length === 0) {
		return null;
	}

	return (
		<div>
			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-8">
				{currentProducts.map((product) => (
					<Link
						key={product.id}
						href={`/product/item/${encodeURIComponent(product.id)}`}
						className="group block border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1.5"
					>
						<div className="relative w-full aspect-square bg-gray-100">
							<Image
								src={product.imageUrl}
								alt={product.name}
								fill
								style={{ objectFit: "cover" }}
								sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
								className="transition-transform duration-300 group-hover:scale-105"
							/>
						</div>
						<div className="p-4 lg:p-5 bg-white text-center">
							<h3 className="text-sm lg:text-base font-bold text-gray-800 transition-colors duration-300 group-hover:text-[#1a3d5d]">
								{product.name}
							</h3>
						</div>
					</Link>
				))}
			</div>

			{totalPages > 1 && (
				<div className="flex justify-center items-center mt-12 space-x-2 md:space-x-4">
					{/* 5. 修改 onClick 事件处理器，调用 onPageChange 函数 */}
					<button
						onClick={() => onPageChange(currentPage - 1)}
						disabled={currentPage === 1}
						className="px-3 py-2 md:px-4 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<ChevronLeft className="w-5 h-5" />
					</button>
					<span className="text-sm text-gray-700">
						Page {currentPage} of {totalPages}
					</span>
					<button
						onClick={() => onPageChange(currentPage + 1)}
						disabled={currentPage === totalPages}
						className="px-3 py-2 md:px-4 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<ChevronRight className="w-5 h-5" />
					</button>
				</div>
			)}
		</div>
	);
}
